import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    Output,
    SimpleChanges
} from '@angular/core';
import {TreeNode, TreeTableNode} from 'primeng/api';
import {TaskTreeViewExtension} from '../../../../api-extensions/views/task-tree.view-extension';
import {PrimeNgImportsModule} from '../../../../../../sdk/src/prime-ng-imports';
import {CommonModule} from '@angular/common';
import {ECpmCTaskStatusApiEnum} from '../../../../../../gen/cpm-c-api-ts/src/enums/c-customer/cpmc-task.api-enums';
import {TaskCRUDComponent} from '../task-crud.component';
import {TreeTableContextMenuSelectEvent} from 'primeng/treetable';
import {IBaseComponent} from '../../../../../../sdk/src/components/ibase/ibase.component';
import {DateUtils} from '../../../../../../sdk/src/utils/date.utils';
import {TaskUtils} from '../../../../utils/task.utils';
import {TreeUtils} from '../../../../../../sdk/src/utils/tree.utils';

// ===============================================================

/** A single day column rendered in the Gantt grid header/body. */
export interface GanttDayColumn {
    date: Date;
    dayLetter: string;
    isWeekend: boolean;
    isToday: boolean;
    weekLabel: string;
}

/** A group of consecutive {@link GanttDayColumn}s belonging to the same week, rendered as one header cell. */
export interface GanttWeekGroup {
    label: string;
    colspan: number;
}

/** Emitted when a task's date(s) have been changed via drag/resize interaction. */
export interface GanttDateChangeEvent {
    task: TaskTreeViewExtension;
    changes: Partial<{start_date: string | null; end_date: string | null; due_date: string | null}>;
}

/** Emitted when the user requests to edit a specific date field of a task (e.g. via clicking the date cell). */
export interface GanttDateEditRequestEvent {
    task: TaskTreeViewExtension;
    field: 'start_date' | 'end_date' | 'due_date';
}

/** Tracks the in-progress mouse drag interaction used to resize/move a task bar or its due date marker. */
interface GanttDragState {
    task: TaskTreeViewExtension;
    field: 'start_date' | 'end_date' | 'move' | 'due_date';
    dragging: boolean;
    /** For move: the column index where the drag started */
    originColIndex?: number;
    /** For move: original start_date when drag began */
    originalStartDate?: string;
    /** For move: original end_date when drag began */
    originalEndDate?: string;
}

// ===============================================================

/**
 * Renders a Gantt chart view of a task tree, allowing tasks to be visualized on a day/week grid and
 * their start/end/due dates to be changed by dragging (moving or resizing) the corresponding bar or marker.
 */
@Component({
    selector: 'app-task-gantt-panel',
    templateUrl: './task-gantt-panel.component.html',
    styleUrls: ['./task-gantt-panel.component.scss'],
    imports: [PrimeNgImportsModule, CommonModule]
})
export class TaskGanttPanelComponent extends IBaseComponent implements OnChanges {
    protected readonly TaskUtils = TaskUtils;

    // ===============================================================
    // ===============================================================

    protected _startDate: Date = new Date();
    protected _searchValue?: string;
    protected searcheableFields: string[] = ['name'];
    protected _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

    // ===============================================================
    // ===============================================================

    @Input()
    public taskCrud?: TaskCRUDComponent;
    /** Task tree to render, as consumed by `p-treetable`. */
    @Input()
    public treeItems: TreeNode<TaskTreeViewExtension>[] = [];
    /** Number of weeks (7-day columns) to render in the grid. */
    @Input()
    public weeksToShow = 3;
    /** Task currently selected in the parent component, used as a fallback when no row has been selected within the Gantt panel itself. */
    @Input()
    public selectedTask: TreeNode<TaskTreeViewExtension> | null = null;

    @Output()
    public startDateLabel = '';
    @Output()
    public dateChange = new EventEmitter<GanttDateChangeEvent>();
    @Output()
    public dateEditRequest = new EventEmitter<GanttDateEditRequestEvent>();
    @Output()
    public taskSelected = new EventEmitter<TaskTreeViewExtension | null>();

    // ===============================================================

    protected dayColumns: GanttDayColumn[] = [];
    protected weekGroups: GanttWeekGroup[] = [];
    protected dragState: GanttDragState | null = null;
    protected ganttSelectedTask: TaskTreeViewExtension | null = null;

    private edgeNavigateTimer: ReturnType<typeof setTimeout> | null = null;
    private readonly EDGE_NAVIGATE_DELAY_MS = 700;
    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onReady(): Promise<void> {}
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /** Recomputes the day/week grid whenever the inputs that affect it change. */
    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['treeItems'] || changes['weeksToShow'] || changes['startDate']) {
            this.computeGanttGrid();
        }
    }

    // ===============================================================
    // ===============================================================

    /**
     * Navigate timeline by weeks
     * @param direction -1 for previous, +1 for next
     */
    public navigate(direction: number): void {
        const newDate = new Date(this._startDate);
        newDate.setDate(newDate.getDate() + direction * 7);

        this._startDate = newDate;

        this.computeGanttGrid();
    }
    /**
     * Reset to today
     */
    public goToToday(): void {
        this._startDate = new Date();
        this.computeGanttGrid();
    }

    // ===============================================================
    // ===============================================================

    /**
     * Compute the Gantt grid columns based on task dates
     */
    private computeGanttGrid(): void {
        const requestedDate = this._startDate ?? new Date();
        const startDate = new Date(requestedDate);
        startDate.setDate(requestedDate.getDate() - ((requestedDate.getDay() + 6) % 7));

        const dayCountToDisplay = this.weeksToShow * 7;
        const now = new Date();
        const todayNum = this.toDateNum(now);

        this.startDateLabel = this.generateWeekRangeLabelForDate(startDate, this.weeksToShow * 7);

        this.dayColumns = [];
        this.weekGroups = [];

        let currentWeekLabel = '';
        let currentWeekColspan = 0;

        for (let i = 0; i < dayCountToDisplay; i++) {
            const currentDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + i);
            const dayOfWeek = currentDay.getDay();
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
            const isToday = this.toDateNum(currentDay) === todayNum;
            const weekLabel = this.generateWeekRangeLabelForDate(currentDay);

            const col: GanttDayColumn = {
                date: currentDay,
                dayLetter: DateUtils.DAYS_TWO_LETTER[dayOfWeek],
                isWeekend,
                isToday,
                weekLabel
            };

            this.dayColumns.push(col);

            if (weekLabel === currentWeekLabel) {
                currentWeekColspan++;
            } else {
                if (currentWeekLabel !== '') {
                    this.weekGroups.push({label: currentWeekLabel, colspan: currentWeekColspan});
                }
                currentWeekLabel = weekLabel;
                currentWeekColspan = 1;
            }
        }

        if (currentWeekLabel !== '') {
            this.weekGroups.push({label: currentWeekLabel, colspan: currentWeekColspan});
        }

        this._changeDetectorRef.detectChanges();
    }

    // ===============================================================
    // ===============================================================

    /**
     * Get week label for a given date and day range (default is 7 days)
     */
    private generateWeekRangeLabelForDate(date: Date, dayRange = 7): string {
        const dayOfWeek = date.getDay();

        const firstDay = new Date(date);
        firstDay.setDate(date.getDate() - ((dayOfWeek + 6) % 7));

        const lastDay = new Date(firstDay);
        lastDay.setDate(firstDay.getDate() + (dayRange - 1));

        const firstDayStr = firstDay.getDate().toString().padStart(2, '0');
        const firstMonthStr = DateUtils.MONTHS_NAME_SHORT[firstDay.getMonth()];
        const firstYearStr = firstDay.getFullYear().toString().slice(-2);

        const lastDayStr = lastDay.getDate().toString().padStart(2, '0');
        const lastMonthStr = DateUtils.MONTHS_NAME_SHORT[lastDay.getMonth()];
        const lastYearStr = lastDay.getFullYear().toString().slice(-2);

        return `${firstDayStr} ${firstMonthStr} ${firstYearStr} - ${lastDayStr} ${lastMonthStr} ${lastYearStr}`;
    }

    // ===============================================================
    // ===============================================================

    /** Convert a `Date` to a comparable numeric form `YYYYMMDD`. */
    private toDateNum(d: Date): number {
        return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
    }

    /** Convert an API date string (`YYYY-MM-DD`) to a comparable numeric form `YYYYMMDD`. */
    private apiDateToNum(dateStr: string): number {
        const d = new Date(dateStr);
        return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
    }

    /** Whether the task's date range (start_date..end_date) covers the given day column. */
    protected isTaskActiveOnDay(task: TaskTreeViewExtension, dayCol: GanttDayColumn): boolean {
        if (!task.start_date) return false;

        const cellNum = this.toDateNum(dayCol.date);
        const startNum = this.apiDateToNum(task.start_date);
        const endNum = task.end_date ? this.apiDateToNum(task.end_date) : startNum;

        return cellNum >= startNum && cellNum <= endNum;
    }

    /** Whether the given day column is the first day of the task's bar (its start_date). */
    protected isBarStart(task: TaskTreeViewExtension, dayCol: GanttDayColumn): boolean {
        if (!task.start_date) return false;
        return this.toDateNum(dayCol.date) === this.apiDateToNum(task.start_date);
    }

    /** Whether the given day column is the last day of the task's bar (its end_date, or start_date if no end_date is set). */
    protected isBarEnd(task: TaskTreeViewExtension, dayCol: GanttDayColumn): boolean {
        if (!task.start_date) return false;
        const endNum = task.end_date ? this.apiDateToNum(task.end_date) : this.apiDateToNum(task.start_date);
        return this.toDateNum(dayCol.date) === endNum;
    }

    /** Whether the given day column matches the task's due_date. */
    protected isDueDate(task: TaskTreeViewExtension, dayCol: GanttDayColumn): boolean {
        if (!task.due_date) return false;
        return this.toDateNum(dayCol.date) === this.apiDateToNum(task.due_date);
    }

    /**
     * Get the label to display at the start/end edge of a task's bar (e.g. "05 Jan"), or an empty
     * string when the given day column is neither the bar's start nor its end.
     */
    protected getBarDateLabel(task: TaskTreeViewExtension, dayCol: GanttDayColumn): string {
        if (this.isBarStart(task, dayCol) && task.start_date) {
            const d = new Date(task.start_date);
            return `${d.getDate()} ${DateUtils.MONTHS_NAME_SHORT[d.getMonth()]}`;
        }
        if (this.isBarEnd(task, dayCol) && task.end_date) {
            const d = new Date(task.end_date);
            return `${d.getDate()} ${DateUtils.MONTHS_NAME_SHORT[d.getMonth()]}`;
        }
        return '';
    }

    /** CSS class for the bar's fill color, based on the task's status. */
    protected getBarColorClass(task: TaskTreeViewExtension): string {
        switch (task.status) {
            case ECpmCTaskStatusApiEnum.SUCCESS:
                return 'gantt-bar-success';
            case ECpmCTaskStatusApiEnum.IN_PROGRESS:
                return 'gantt-bar-in-progress';
            case ECpmCTaskStatusApiEnum.FAILED:
                return 'gantt-bar-failed';
            case ECpmCTaskStatusApiEnum.READY:
                return 'gantt-bar-ready';
            default:
                return 'gantt-bar-created';
        }
    }

    // ===============================================================
    // Drag & drop resize
    // ===============================================================

    /**
     * Determine which interaction zone of a task's bar the mouse is over within the given day cell:
     * 'start'/'end' for resize handles near the bar's edges, 'move' for the middle, or `null` when
     * the task has no bar on this day.
     */
    protected getBarZone(
        task: TaskTreeViewExtension,
        day: GanttDayColumn,
        event: MouseEvent
    ): 'start' | 'end' | 'move' | null {
        if (!this.isTaskActiveOnDay(task, day)) return null;
        const target = event.target as HTMLElement;
        const cell = target.closest('.gantt-cell') as HTMLElement;
        if (!cell) return null;
        const rect = cell.getBoundingClientRect();
        const xInCell = event.clientX - rect.left;

        const isStart = this.isBarStart(task, day);
        const isEnd = this.isBarEnd(task, day);

        if (isStart && isEnd) {
            // Single-cell bar: left 30% = resize start, middle 40% = move, right 30% = resize end
            if (xInCell < rect.width * 0.3) return 'start';
            if (xInCell > rect.width * 0.7) return 'end';
            return 'move';
        }

        if (isStart && xInCell < rect.width * 0.4) return 'start';
        if (isEnd && xInCell > rect.width * 0.6) return 'end';

        // Middle of the bar → move zone
        return 'move';
    }

    /** Updates the drag preview while dragging, or the mouse cursor to hint the available interaction zone otherwise. */
    protected onCellMouseMove(task: TaskTreeViewExtension, day: GanttDayColumn, event: MouseEvent): void {
        if (this.dragState?.dragging) {
            this.applyDrag(task, day, false);
            this.autoNavigateIfAtEdge(day);
            return;
        }
        const isDueDateMarker = this.isDueDateMarkerTarget(task, day, event);
        const zone = this.getBarZone(task, day, event);
        const cell = (event.target as HTMLElement).closest('.gantt-cell') as HTMLElement;
        if (cell) {
            if (isDueDateMarker) {
                cell.style.cursor = 'col-resize';
            } else if (zone === 'start' || zone === 'end') {
                cell.style.cursor = 'col-resize';
            } else if (zone === 'move') {
                cell.style.cursor = 'grab';
            } else {
                cell.style.cursor = '';
            }
        }
    }

    /**
     * Selects the task and starts a drag interaction (resize, move, or due-date drag) when the click
     * lands on an interactive zone, or assigns a start/end date to an unscheduled selected task.
     */
    protected onCellMouseDown(task: TaskTreeViewExtension, day: GanttDayColumn, event: MouseEvent): void {
        if (this.dragState) return;
        this.selectTask(task);

        if (this.isDueDateMarkerTarget(task, day, event)) {
            event.preventDefault();
            this.dragState = {task, field: 'due_date', dragging: true};
            document.body.style.cursor = 'col-resize';
            this.registerDragEnd();
            return;
        }

        // If task has a bar, handle drag resize or move
        const zone = this.getBarZone(task, day, event);
        if (zone) {
            event.preventDefault();
            const colIndex = this.dayColumns.indexOf(day);

            if (zone === 'move') {
                this.dragState = {
                    task,
                    field: 'move',
                    dragging: true,
                    originColIndex: colIndex,
                    originalStartDate: task.start_date,
                    originalEndDate: task.end_date
                };
                document.body.style.cursor = 'grabbing';
            } else {
                this.dragState = {
                    task,
                    field: zone === 'start' ? 'start_date' : 'end_date',
                    dragging: true
                };
            }

            this.registerDragEnd();
            return;
        }

        // If task has no dates and this row matches the selected task, assign date on click
        if (!task.start_date && !task.end_date && this.isSelectedTask(task)) {
            const dateStr = this.dateToApiString(day.date);
            task.start_date = dateStr;
            task.end_date = dateStr;
            this.dateChange.emit({task, changes: {start_date: dateStr, end_date: dateStr}});
        }
    }

    /** Finalizes and persists the current drag interaction, if any. */
    protected onCellMouseUp(task: TaskTreeViewExtension, day: GanttDayColumn): void {
        if (this.dragState?.dragging) {
            this.cancelEdgeNavigate();
            this.applyDrag(task, day, true);
            this.dragState = null;
        }
    }

    /** Dispatches the active drag interaction to the matching apply handler based on its field. */
    private applyDrag(_hoverTask: TaskTreeViewExtension, day: GanttDayColumn, persist: boolean): void {
        if (!this.dragState) return;
        const task = this.dragState.task;
        const field = this.dragState.field;

        if (field === 'move') {
            this.applyMove(task, day, persist);
        } else if (field === 'due_date') {
            this.applyDueDate(task, day, persist);
        } else {
            this.applyResize(task, field, day, persist);
        }
    }

    /** Registers a one-time document-level `mouseup` listener to safely end a drag interaction outside the grid. */
    private registerDragEnd(): void {
        const onMouseUp = () => {
            this.cancelEdgeNavigate();
            this.dragState = null;
            document.body.style.cursor = '';
            document.removeEventListener('mouseup', onMouseUp);
        };
        document.addEventListener('mouseup', onMouseUp);
    }

    private isDueDateMarkerTarget(task: TaskTreeViewExtension, day: GanttDayColumn, event: MouseEvent): boolean {
        return this.isDueDate(task, day) && !!(event.target as HTMLElement).closest('.gantt-due-marker');
    }

    /** Applies a due-date drag by setting the task's due_date to the day being hovered/dropped on. */
    private applyDueDate(task: TaskTreeViewExtension, day: GanttDayColumn, persist: boolean): void {
        const newDateStr = this.dateToApiString(day.date);
        task.due_date = newDateStr;
        if (persist) {
            this.dateChange.emit({task, changes: {due_date: newDateStr}});
        }
    }

    /** Applies a bar move by shifting both start_date and end_date by the number of days dragged. */
    private applyMove(task: TaskTreeViewExtension, day: GanttDayColumn, persist: boolean): void {
        const currentColIndex = this.dayColumns.indexOf(day);
        const delta = currentColIndex - (this.dragState!.originColIndex ?? currentColIndex);

        const origStart = new Date(this.dragState!.originalStartDate!);
        const origEnd = new Date(this.dragState!.originalEndDate ?? this.dragState!.originalStartDate!);
        const newStart = new Date(origStart.getFullYear(), origStart.getMonth(), origStart.getDate() + delta);
        const newEnd = new Date(origEnd.getFullYear(), origEnd.getMonth(), origEnd.getDate() + delta);

        const newStartStr = this.dateToApiString(newStart);
        const newEndStr = this.dateToApiString(newEnd);

        task.start_date = newStartStr;
        task.end_date = newEndStr;

        if (persist && delta !== 0) {
            this.dateChange.emit({task, changes: {start_date: newStartStr, end_date: newEndStr}});
        }
    }

    /** Applies a start/end resize, keeping start_date/end_date consistent (clamping the other bound if crossed). */
    private applyResize(
        task: TaskTreeViewExtension,
        field: 'start_date' | 'end_date',
        day: GanttDayColumn,
        persist: boolean
    ): void {
        const newDateStr = this.dateToApiString(day.date);
        const changes: GanttDateChangeEvent['changes'] = {};

        if (field === 'start_date') {
            task.start_date = newDateStr;
            changes.start_date = newDateStr;
            if (task.end_date && this.apiDateToNum(task.start_date) > this.apiDateToNum(task.end_date)) {
                task.end_date = newDateStr;
                changes.end_date = newDateStr;
            }
        } else {
            task.end_date = newDateStr;
            changes.end_date = newDateStr;
            if (task.start_date && this.apiDateToNum(task.end_date) < this.apiDateToNum(task.start_date)) {
                task.start_date = newDateStr;
                changes.start_date = newDateStr;
            }
        }

        if (persist) {
            this.dateChange.emit({task, changes});
        }
    }

    /** Format a `Date` as an API date string (`YYYY-MM-DD`). */
    private dateToApiString(d: Date): string {
        return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
    }

    /** While dragging, schedules a grid navigation request when the cursor lingers on the first/last visible day column. */
    private autoNavigateIfAtEdge(day: GanttDayColumn): void {
        const dayIndex = this.dayColumns.indexOf(day);
        const isAtLeftEdge = dayIndex === 0;
        const isAtRightEdge = dayIndex === this.dayColumns.length - 1;

        if (!isAtLeftEdge && !isAtRightEdge) {
            this.cancelEdgeNavigate();
            return;
        }

        if (this.edgeNavigateTimer) return;

        const direction = isAtLeftEdge ? -1 : 1;
        this.edgeNavigateTimer = setTimeout(() => {
            this.edgeNavigateTimer = null;
            if (this.dragState?.dragging) {
                this.navigate(direction);
            }
        }, this.EDGE_NAVIGATE_DELAY_MS);
    }

    /** Cancels a pending edge-navigation timer, if any. */
    private cancelEdgeNavigate(): void {
        if (this.edgeNavigateTimer) {
            clearTimeout(this.edgeNavigateTimer);
            this.edgeNavigateTimer = null;
        }
    }

    /** Selects the task and requests editing the clicked date field via an external control. */
    protected onDateCellClick(task: TaskTreeViewExtension, field: 'start_date' | 'end_date' | 'due_date'): void {
        this.selectTask(task);
        this.dateEditRequest.emit({task, field});
    }

    private selectTask(task: TaskTreeViewExtension): void {
        this.ganttSelectedTask = task;
        this.selectedTask = TreeUtils.findTreeNodeByKey(this.treeItems, task.uuid) ?? null;
        this.taskSelected.emit(task);
    }

    /**
     * Whether the given task is currently selected: uses the internal Gantt selection when set,
     * otherwise falls back to the parent's `selectedTask` input.
     */
    protected isSelectedTask(task: TaskTreeViewExtension): boolean {
        if (this.ganttSelectedTask) {
            return this.ganttSelectedTask.uuid === task.uuid;
        }
        return !!this.selectedTask?.data && this.selectedTask.data.uuid === task.uuid;
    }

    /** Whether the task has no dates yet and is selected, allowing its date to be assigned by clicking a day cell. */
    protected isAssignableCell(task: TaskTreeViewExtension): boolean {
        return !task.start_date && !task.end_date && this.isSelectedTask(task);
    }

    // ===============================================================
    // ===============================================================

    /**
     * When the selection change
     * @param selection New selection
     */
    protected selectionChange(
        selection: TreeTableNode<TaskTreeViewExtension> | TreeTableNode<TaskTreeViewExtension>[] | null
    ) {
        if (Array.isArray(selection)) {
            return;
        }

        this.taskSelected.next(selection?.data ?? null);
    }
    /**
     * When the context menu is being show for a node
     */
    protected contextMenuSelect(data: TreeTableContextMenuSelectEvent) {
        this.selectedTask = data.node;
        this.selectionChange(data.node ? data.node : null);
        this.taskCrud?.onContextMenuSelect(data.node.data!);
    }
}
